// Currency — conversor multi-moneda offline-friendly con detección automática por GPS.
// Las 4 monedas pedidas + EUR (Madrid) + CLP (referencia desde Chile).
import { Link } from "react-router-dom";
import { ChevronLeft, RefreshCw, Wifi, WifiOff, MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useCurrencyRates, convertAmount } from "../hooks/useCurrencyRates";
import { useGeolocation } from "../hooks/useGeolocation";
import { detectCity } from "../hooks/useCityDetection";
import { useTripBase } from "../hooks/useTripBase";

const CURRENCIES = [
  { code: "USD", name: "Dólar EE.UU.", flag: "🇺🇸", symbol: "US$" },
  { code: "EUR", name: "Euro", flag: "🇪🇺", symbol: "€" },
  { code: "HUF", name: "Florín húngaro", flag: "🇭🇺", symbol: "Ft" },
  { code: "CZK", name: "Corona checa", flag: "🇨🇿", symbol: "Kč" },
  { code: "ILS", name: "Shekel israelí", flag: "🇮🇱", symbol: "₪" },
  { code: "CLP", name: "Peso chileno", flag: "🇨🇱", symbol: "CLP$" },
];

function formatNumber(n, code) {
  if (n == null || !Number.isFinite(n)) return "—";
  // HUF y CLP sin decimales (monedas grandes)
  const noDecimals = code === "HUF" || code === "CLP";
  const opts = noDecimals
    ? { maximumFractionDigits: 0 }
    : { maximumFractionDigits: 2, minimumFractionDigits: 2 };
  return new Intl.NumberFormat("es-CL", opts).format(n);
}

function formatUpdated(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString("es-CL", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Currency() {
  const base = useTripBase();
  const { rates, updatedAt, source, loading } = useCurrencyRates();
  const { position } = useGeolocation();
  const [baseCode, setBaseCode] = useState("EUR");
  const [amount, setAmount] = useState("100");
  const [autoSwitched, setAutoSwitched] = useState(null); // {city, currency}
  const userOverrodeRef = useRef(false);

  // Auto-switch a la moneda local según GPS — solo si el usuario no eligió manualmente
  useEffect(() => {
    if (userOverrodeRef.current) return;
    const detected = detectCity(position);
    if (detected && detected.currency !== baseCode) {
      setBaseCode(detected.currency);
      setAutoSwitched(detected);
    } else if (detected) {
      setAutoSwitched(detected);
    }
  }, [position]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (newCode, newAmount) => {
    userOverrodeRef.current = true;
    setBaseCode(newCode);
    setAmount(newAmount);
  };

  return (
    <div className="container-app pt-6 pb-28" data-testid="currency-page">
      <Link
        to={base || "/"}
        className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4"
        data-testid="currency-back"
      >
        <ChevronLeft size={16} /> Inicio
      </Link>
      <h1 className="text-3xl font-bold leading-tight">Cambio de moneda</h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">
        Conversor rápido entre las monedas de tu viaje
      </p>

      {/* Estado de cotizaciones */}
      <div className="card-paper p-3 mb-4 flex items-center justify-between gap-3" data-testid="currency-status">
        <div className="flex items-center gap-2 text-xs">
          {source === "live" ? (
            <Wifi size={14} className="text-[hsl(var(--accent))]" />
          ) : (
            <WifiOff size={14} className="text-[hsl(var(--muted))]" />
          )}
          <div>
            <p className="font-semibold leading-tight">
              {source === "live" && "Cotizaciones en vivo"}
              {source === "cache" && "Cotizaciones guardadas (offline)"}
              {source === "fallback" && "Cotizaciones aproximadas"}
            </p>
            <p className="text-[10px] text-[hsl(var(--muted))]">
              Actualizado: {formatUpdated(updatedAt)}
            </p>
          </div>
        </div>
        {loading && <RefreshCw size={14} className="animate-spin text-[hsl(var(--muted))]" />}
      </div>

      {/* Badge de ciudad detectada */}
      {autoSwitched && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--accent))] mb-3 px-1"
          data-testid="currency-auto-detected"
        >
          <MapPin size={12} />
          <span>
            Estás en <strong>{autoSwitched.city}</strong> — moneda base ajustada a{" "}
            <strong>{autoSwitched.currency}</strong>
          </span>
        </motion.div>
      )}

      {/* Lista de monedas */}
      <ul className="space-y-2.5">
        {CURRENCIES.map((c, i) => {
          const isBase = c.code === baseCode;
          const value = isBase
            ? amount
            : (() => {
                const converted = convertAmount(amount, baseCode, c.code, rates);
                return converted != null ? formatNumber(converted, c.code) : "—";
              })();

          return (
            <motion.li
              key={c.code}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.04 * i }}
              className={`card-paper p-3 flex items-center gap-3 transition-all ${
                isBase ? "ring-2 ring-[hsl(var(--accent))]" : ""
              }`}
              data-testid={`currency-row-${c.code}`}
            >
              <span className="text-2xl shrink-0" aria-hidden>
                {c.flag}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">{c.name}</p>
                <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">
                  {c.code} · {c.symbol}
                </p>
              </div>
              <div className="shrink-0 w-[42%] max-w-[180px]">
                {isBase ? (
                  <input
                    type="number"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => handleChange(c.code, e.target.value)}
                    onFocus={() => setBaseCode(c.code)}
                    className="w-full text-right text-lg font-bold bg-transparent border-b-2 border-[hsl(var(--accent))] focus:outline-none px-1 py-1"
                    data-testid={`currency-input-${c.code}`}
                  />
                ) : (
                  <button
                    onClick={() => {
                      // al tocar otra fila, esa pasa a ser la base con el valor convertido actual
                      const converted = convertAmount(amount, baseCode, c.code, rates);
                      if (converted != null) {
                        const noDec = c.code === "HUF" || c.code === "CLP";
                        const rounded = noDec
                          ? Math.round(converted).toString()
                          : (Math.round(converted * 100) / 100).toString();
                        handleChange(c.code, rounded);
                      } else {
                        setBaseCode(c.code);
                      }
                    }}
                    className="w-full text-right text-lg font-semibold text-[hsl(var(--ink-soft))] px-1 py-1 hover:text-[hsl(var(--ink))] active:scale-95"
                    data-testid={`currency-value-${c.code}`}
                  >
                    {value}
                  </button>
                )}
              </div>
            </motion.li>
          );
        })}
      </ul>

      {/* Atajos */}
      <div className="mt-5">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-2">
          Atajos
        </p>
        <div className="flex flex-wrap gap-2">
          {[10, 50, 100, 500, 1000].map((v) => (
            <button
              key={v}
              onClick={() => setAmount(String(v))}
              className="px-3 py-1.5 rounded-full bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] text-xs font-medium text-[hsl(var(--ink-soft))] active:scale-95"
              data-testid={`currency-shortcut-${v}`}
            >
              {v} {baseCode}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-[hsl(var(--muted))] mt-6 leading-relaxed">
        💡 Las cotizaciones son referenciales. En cajeros y casas de cambio se aplica un margen
        (típicamente 1–3 %). Para pagos con tarjeta, su banco aplica el tipo del día +1 %.
      </p>
      <p className="text-[10px] text-[hsl(var(--muted))] mt-2 italic">
        Datos: open.er-api.com (actualización diaria). Funciona offline con los últimos valores
        guardados.
      </p>
    </div>
  );
}
